// src/services/AdminService.js

import { API_BASE_URL } from '../utils/constants';
import axiosInstance from './axiosInstance';

// User Authentication API functions
export const AdminService = {
	login: async (email, password) => {
		try {
			const response = await fetch(`${API_BASE_URL}/auth/admin/login`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ email, password })
			});

			const data = await response.json();

			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	addSubAdmin: async ({ firstName, lastName, email, roleId, effectivePermissions }) => {
		try {
			const response = await axiosInstance.post(`${API_BASE_URL}/user`, {
				firstName,
				lastName,
				email,
				roleId,
				effectivePermissions
			});

			const data = await response.data;

			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	updateSubAdmin: async (userId, dataToUpdate) => {
		try {
			const formData = new FormData();
			formData.append('userId', userId);
			formData.append('self', false); // Assuming `self: true` for sub-admin updates as well

			// Append other fields from dataToUpdate
			for (const key in dataToUpdate) {
				if (dataToUpdate.hasOwnProperty(key)) {
					if (key === 'effectivePermissions' && Array.isArray(dataToUpdate[key])) {
						// If effectivePermissions is an array, append each item individually
						dataToUpdate[key].forEach((permission) => {
							formData.append(`${key}[]`, permission);
						});
					} else {
						formData.append(key, dataToUpdate[key]);
					}
				}
			}

			const response = await axiosInstance.put(`${API_BASE_URL}/user`, formData);
			const data = await response.data;
			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	deleteSubAdmin: async (userId) => {
		try {
			const response = await axiosInstance.delete(`${API_BASE_URL}/user/${userId}`);
			const data = await response.data;
			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	getSubAdmins: async () => {
		try {
			const response = await axiosInstance.get(`${API_BASE_URL}/user`);
			const data = await response.data;

			return {
				status: response.status,
				data: data
			};
		} catch (error) {
			throw error;
		}
	},

	updateProfile: async (id, profileData) => {
		try {
			const formData = new FormData();

			// Add userId and self: true to formData
			formData.append('userId', id);
			formData.append('self', true);

			if (profileData.firstName) {
				formData.append('firstName', profileData.firstName);
			}
			if (profileData.lastName) {
				formData.append('lastName', profileData.lastName);
			}

			if (profileData.profileImage) {
				formData.append('profileImage', profileData.profileImage);
			}

			// The URL is now fixed to /user
			const response = await axiosInstance.put(`${API_BASE_URL}/user`, formData);
			const data = await response.data;

			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	changePassword: async (oldPassword, newPassword) => {
		try {
			const response = await axiosInstance.put('/user/change-password', {
				oldPassword,
				newPassword
			});

			const data = response.data;

			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	sendResetPasswordEmail: async (email) => {
		try {
			const response = await fetch(`${API_BASE_URL}/user/send-reset-password-email`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ email })
			});

			const data = await response.json();

			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	resetPassword: async (email, token, newPassword) => {
		try {
			const response = await fetch(`${API_BASE_URL}/user/reset-password`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ email, token, newPassword })
			});

			const data = await response.json();

			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	getUsersCount: async (isActive) => {
		try {
			const response = await axiosInstance.get(`${API_BASE_URL}/customer/count/${isActive}`);
			const data = await response.data;
			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	},

	getDocumentsCount: async (count) => {
		try {
			const response = await axiosInstance.get(`${API_BASE_URL}/document/all/${count}`);
			const data = await response.data;
			return {
				status: response.status,
				data: data,
				...data
			};
		} catch (error) {
			throw error;
		}
	}
};
